import React, { useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ByteChip from "../../../icons/ByteChip";
import Button from "@mui/material/Button";
import AccountBalanceTooltip from "./AccountBalanceTooltip";
import { Box, Fade, IconButton, Modal } from "@mui/material";
import GiftIcon from "../../../icons/GiftIcon";
import LogoImage from "../../../../assets/ByteChip.png";
import TopUpForm, { TopUpParams } from "../../../formComponents/TopUpForm";
import CancelIcon from "../../../icons/Cancel";
import CustomModal from "../../modal/CustomModal";
import ByteTextField from "../../../formComponents/ByteTextField";
import { ArrowForward } from "@mui/icons-material";
import { RedeemCouponResponse } from "../../../formComponents/PromoCodeForm";
import { any, string } from "prop-types";
import RightIcon from "../../../icons/RightIcon";
import ResponseCard from "../ResponseCard";
interface Error {
  code: number;
  message: string;
}
const AccountBalance = (props: {
  balance: number;
  amountDue: number;
  handelExchangeFn: (value: number) => void;
  exchangeRateData: {
    amount: number;
    currencyAmount: number;
    exchangeRate: number;
  } | null;
  setExchangeRate: (
    value: {
      amount: number;
      currencyAmount: number;
      exchangeRate: number;
    } | null
  ) => void;
  onRedeemCoupon: (value: string) => Promise<RedeemCouponResponse>;
  onTopUpRequest: (currency: string, amount: number) => Promise<TopUpParams>;
}) => {
  const {
    balance,
    amountDue,
    handelExchangeFn,
    setExchangeRate,
    exchangeRateData,
    onRedeemCoupon,
    onTopUpRequest,
  } = props;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isRedeemMode, setIsRedeemMode] = useState<boolean>(false);
  const [codeInput, setCodeInput] = useState("");
  const [redeemErrorMessage, setRedeemErrorMessage] = useState("");
  const [redeemError, setRedeemError] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [redeemAmount, setRedeemAmount] = useState<number | undefined>(0);
  const [loadingRedeemBtn, setLoadingRedeemBtn] = useState(false);
  const dueDate: string = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    1
  ).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const todayDate: string = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  ).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const handleModalOpen = (redeemMode: boolean) => {
    setIsRedeemMode(redeemMode);
    setCurrentStep(0);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setExchangeRate(null);
    setRedeemError(false);
    setRedeemErrorMessage("");
    setCodeInput("");
  };
  const handleSubmitRedeem = () => {
    setLoadingRedeemBtn(true);
    if (codeInput === "") {
      setRedeemError(true);
      setRedeemErrorMessage("This field is required");
      return;
    }
    onRedeemCoupon(codeInput)
      .then((resp) => {
        setRedeemAmount(resp.amount);
        setCurrentStep(1);
      })
      .catch((err) => {
        setCurrentStep(0);
        if (err?.json) {
          err?.json().then((errVal: Error) => {
            if (errVal.code === 5) {
              setRedeemError(true);
              setRedeemErrorMessage("Code not found");
            }
            if (errVal.code === 13) {
              setRedeemError(true);
              setRedeemErrorMessage(
                "only coupons with quantity > 0 can be redeemed"
              );
            }
          });
        }
      });
  };
  const handleComponentRender = (data: number) => {
    switch (data) {
      case 0:
        return isRedeemMode ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography textAlign="center">
              Insert your coupon code below
            </Typography>

            <ByteTextField
              label={"Code"}
              required
              sx={{ mt: 3 }}
              onChangeValue={setCodeInput}
              error={redeemError}
              errorMessage={redeemErrorMessage}
            />
            <Button
              // disabled={loadingRedeemBtn}
              type="button"
              sx={{
                mt: 2,
              }}
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={handleSubmitRedeem}
            >
              Redeem
            </Button>
          </Box>
        ) : (
          <TopUpForm
            onChipSelect={handelExchangeFn}
            exchangeData={exchangeRateData}
            onTopUpRequest={onTopUpRequest}
          />
        );
      case 1:
        return (
          <ResponseCard
            message={
              <>
                <Stack direction={"row"} columnGap={1} flexWrap={"wrap"}>
                  <Typography>The amount of</Typography>
                  <Stack direction={"row"} alignItems={"center"}>
                    <ByteChip size="xxs" />
                    <Typography>{redeemAmount}</Typography>
                  </Stack>
                  <Typography> will be added to your</Typography>
                </Stack>
                <Typography>
                  balance shortly. You may close this modal.
                </Typography>
              </>
            }
            title="Your Coupon has been redeemed!"
            icon={<RightIcon color="success" size="large" />}
          />
        );
    }
  };
  const checkNumberReturnFixedValue = (num: number | null | undefined) => {
    if (num === 0) return "-";
    if (num === null || num === undefined || isNaN(num)) return "-";

    const numStr = num.toString();
    const decimalPart = numStr.split(".")[1];

    if (decimalPart && decimalPart.length > 2) {
      return num.toFixed(2);
    }

    return numStr;
  };
  return (
    <>
      <Card
        sx={{
          maxHeight: "286px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
        }}
      >
        <CardContent>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography
              color="#000271"
              fontSize="18px"
              lineHeight="32px"
              fontWeight={600}
              fontFamily="Gilroy"
            >
              Account Balance
            </Typography>
            <Box sx={{ display: "flex", columnGap: 2, flexWrap: "noWrap" }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleModalOpen(true)}
                sx={{
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  outline: "none",
                  "&:hover": {
                    border: "none",
                    backgroundColor: "transparent",
                  },
                }}
                startIcon={<GiftIcon />}
              >
                <Typography mt={"2px"}>Redeem</Typography>
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleModalOpen(false)}
                color="primary"
                startIcon={<ByteChip color="primary" size="small" />}
              >
                Top Up
              </Button>
            </Box>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
            gap={2}
          >
            <Card
              sx={{
                width: "100%",
                height: "170px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.25)",
              }}
            >
              <Typography
                color="#000000"
                fontSize="16px"
                lineHeight="24px"
                fontWeight={500}
                fontFamily="Gilroy"
              >
                Current Balance
              </Typography>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Typography color="#000271" variant="h1">
                  {balance < 0 ? `-` : ``}
                </Typography>
                <ByteChip color="secondary" colorVariant="dark" size="medium" />
                <Typography color="#000271" variant="h1">
                  {Math.round(Math.abs(balance)).toLocaleString()}
                </Typography>
              </Stack>

              <Typography
                color="#000000"
                fontSize="10px"
                lineHeight="16px"
                fontWeight={500}
                fontFamily="Gilroy"
              >
                as of
              </Typography>
              <Typography
                color="#000000"
                fontSize="16px"
                lineHeight="24px"
                fontWeight={500}
                fontFamily="Gilroy"
              >
                {todayDate}
              </Typography>
            </Card>
            <Card
              sx={{
                width: "100%",
                height: "170px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.25)",
              }}
            >
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Typography
                  color="#000000"
                  fontSize="16px"
                  lineHeight="24px"
                  fontWeight={500}
                  fontFamily="Gilroy"
                >
                  Amount Due
                </Typography>
                <AccountBalanceTooltip />
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Typography color="#000271" variant="h1">
                  ${checkNumberReturnFixedValue(amountDue)}
                </Typography>
              </Stack>
              <Typography
                color="#000000"
                fontSize="10px"
                lineHeight="16px"
                fontWeight={500}
                fontFamily="Gilroy"
              >
                due on
              </Typography>
              <Typography
                color="#000000"
                fontSize="16px"
                lineHeight="24px"
                fontWeight={500}
                fontFamily="Gilroy"
              >
                {amountDue === 0 ||
                amountDue === null ||
                amountDue === undefined
                  ? "-"
                  : dueDate}
              </Typography>
            </Card>
          </Stack>
        </CardContent>
      </Card>
      <CustomModal
        title={isRedeemMode ? "Redeem Coupon" : "TOP UP"}
        handleClose={handleClose}
        openModal={openModal}
      >
        {handleComponentRender(currentStep)}
      </CustomModal>
    </>
  );
};

export default AccountBalance;
